"use client"

import { useEffect, useRef, useState, useSyncExternalStore } from "react"
import { usePathname } from "next/navigation"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import {
    OPEN_SETTINGS_EVENT,
    ConsentChoice,
    analyticsAllowed,
    clearAnalyticsCookies,
    disableAnalytics,
    enableAnalytics,
    getConsentSnapshot,
    getServerConsentSnapshot,
    saveConsent,
    subscribeConsent,
    trackPageView,
} from "@/lib/analytics"
import { useConsentServices } from "@/components/analytics/analytics-config"

/** The box's title and text for the services that are switched on. */
function bannerText(analytics: boolean, maps: boolean) {
    if (analytics && maps) {
        return {
            title: "Cookies, analytics and maps",
            detail:
                "May we use Google Analytics cookies to count visits, and show Google maps on listing pages? No names, emails or phone numbers are sent.",
            short: "Google Analytics and Google Maps load only if you accept.",
        }
    }
    if (maps) {
        return {
            title: "Cookies and maps",
            detail: "May we show Google maps of the properties on listing pages? Google may set its own cookies.",
            short: "Google Maps loads only if you accept.",
        }
    }
    return {
        title: "Cookies and analytics",
        detail:
            "May we use Google Analytics cookies to count visits and see which pages help? No names, emails or phone numbers are sent.",
        short: "Google Analytics loads only if you accept.",
    }
}

/**
 * Consent banner, Google Analytics loader and the map's consent for the public pages.
 * Renders nothing, loads nothing and sets no cookies when neither a measurement ID nor a
 * Maps key is set.
 */
export default function Analytics() {
    const pathname = usePathname()
    const { measurementId, mapsEnabled } = useConsentServices()
    // "pending" on the server and during hydration: storage is never read there.
    const consent = useSyncExternalStore(subscribeConsent, getConsentSnapshot, getServerConsentSnapshot)
    const [reopened, setReopened] = useState(false)
    const headingRef = useRef<HTMLHeadingElement>(null)
    const bannerRef = useRef<HTMLElement>(null)
    const id = consent === "pending" ? "" : measurementId
    const active = consent !== "pending" && (measurementId !== "" || mapsEnabled)
    // An older acceptance covered Google Analytics only: ask once more while the map is on.
    const needsAnswer = consent === "none" || (consent === "granted" && mapsEnabled)
    const open = active && (needsAnswer || reopened)

    // "Cookie settings" in the footer reopens the banner and moves focus to it.
    useEffect(() => {
        if (!active) return
        const reopen = () => setReopened(true)
        window.addEventListener(OPEN_SETTINGS_EVENT, reopen)
        return () => window.removeEventListener(OPEN_SETTINGS_EVENT, reopen)
    }, [active])

    useEffect(() => {
        if (reopened) headingRef.current?.focus()
    }, [reopened])

    // While the banner is open, reserve its height at the bottom of the page (phones, where
    // it spans the full width) so the last content can always be scrolled clear of it.
    useEffect(() => {
        const banner = bannerRef.current
        if (!open || !banner) return
        const body = document.body
        const apply = () => {
            const fullWidth = getComputedStyle(banner).left === "0px" && banner.offsetWidth >= window.innerWidth - 1
            body.style.paddingBottom = fullWidth ? `${banner.offsetHeight}px` : ""
        }
        apply()
        const observer = new ResizeObserver(apply)
        observer.observe(banner)
        window.addEventListener("resize", apply)
        return () => {
            observer.disconnect()
            window.removeEventListener("resize", apply)
            body.style.paddingBottom = ""
        }
    }, [open])

    // Load GA once consent is granted; send a page view on load and on every navigation.
    useEffect(() => {
        if (!id || !analyticsAllowed(consent)) return
        enableAnalytics(id)
        trackPageView()
    }, [id, consent, pathname])

    // Declined (here or in another tab): stop and clear any cookies left behind.
    useEffect(() => {
        if (consent !== "denied") return
        // Also when only the map is on: the box says the _ga cookies are removed on Decline.
        if (id) disableAnalytics(id)
        else clearAnalyticsCookies()
    }, [id, consent])

    function decide(value: ConsentChoice) {
        setReopened(false)
        saveConsent(value, mapsEnabled)
    }

    if (!open) return null

    const text = bannerText(measurementId !== "", mapsEnabled)

    return (
        <section
            ref={bannerRef}
            aria-labelledby="cookie-banner-title"
            className="fixed inset-x-0 bottom-0 z-50 border-t border-zinc-300 bg-white p-2 text-xs text-zinc-900 shadow-lg sm:inset-x-auto sm:bottom-4 sm:left-4 sm:max-w-sm sm:rounded-lg sm:border sm:p-3 sm:text-sm"
        >
            {/* On phones the title is kept for screen readers only, to keep the banner short. */}
            <h2 id="cookie-banner-title" ref={headingRef} tabIndex={-1} className="sr-only font-semibold outline-none sm:not-sr-only">
                {text.title}
            </h2>
            <p className="sm:mt-1">
                <span className="hidden sm:inline">{text.detail} </span>
                {text.short}
            </p>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 sm:mt-2">
                <Button size="sm" className="h-7 px-3 sm:h-8" onClick={() => decide("granted")}>
                    Accept
                </Button>
                <Button size="sm" variant="outline" className="h-7 border-zinc-400 bg-white px-3 text-zinc-900 hover:bg-zinc-100 sm:h-8" onClick={() => decide("denied")}>
                    Decline
                </Button>
                <Link
                    href="/privacy"
                    className="text-zinc-900 underline underline-offset-2 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                    Learn more
                </Link>
            </div>
        </section>
    )
}
